import { RuntimeHostSettings } from './environment.types';

const APP_SETTINGS_STORAGE_KEY = 'app-settings';
const APP_USER_STORAGE_KEY = 'app-user';
const APP_SELECTION_STORAGE_KEY = 'app-selection';

function toObject(value: unknown): Record<string, any> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, any>
        : {};
}

function readStorageObject(storageKey: string): RuntimeHostSettings {
    if (typeof localStorage === 'undefined') {
        return {};
    }

    const raw = localStorage.getItem(storageKey);
    if (!raw) {
        return {};
    }

    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed as RuntimeHostSettings : {};
    } catch {
        localStorage.removeItem(APP_SETTINGS_STORAGE_KEY);
        return {};
    }
}

function readAppSettingsFromStorage(): RuntimeHostSettings {
    return readStorageObject(APP_SETTINGS_STORAGE_KEY);
}

function readAppUserFromStorage(): RuntimeHostSettings {
    return readStorageObject(APP_USER_STORAGE_KEY);
}

function readAppSelectionFromStorage(): RuntimeHostSettings {
    return readStorageObject(APP_SELECTION_STORAGE_KEY);
}

function readWindowRuntimeSettings(): RuntimeHostSettings {
    if (typeof window === 'undefined') {
        return {};
    }

    const legacySettings = (window as any).settings;
    const legacyObject = legacySettings && typeof legacySettings === 'object'
        ? legacySettings as RuntimeHostSettings
        : {};

    return {
        ...legacyObject,
        url: window.url ?? legacyObject.url,
        layout: window.layout ?? legacyObject.layout,
        support: window.support ?? legacyObject.support,
        app: window.app ?? legacyObject.app,
        organizations: window.organizations ?? legacyObject.organizations,
        user: window.user ?? legacyObject.user,
        access: window.access ?? legacyObject.access,
        roles: window.roles ?? legacyObject.roles,
        selection: window.selection ?? legacyObject.selection
    };
}

function writeWindowRuntimeSettings(settings: RuntimeHostSettings): void {
    if (typeof window === 'undefined') {
        return;
    }

    window.url = settings.url;
    window.layout = settings.layout;
    window.support = settings.support;
    window.app = settings.app;
    window.organizations = settings.organizations;
    window.user = settings.user;
    window.access = settings.access;
    window.roles = settings.roles;
    window.selection = settings.selection;

    // Keep compatibility with legacy consumers still reading window.settings.
    (window as any).settings = settings;
}

export function readRuntimeSettings(): RuntimeHostSettings {
    const hostSettings: RuntimeHostSettings = readWindowRuntimeSettings();
    const storedSettings = readAppSettingsFromStorage();
    const storedUser = readAppUserFromStorage();
    const storedSelection = readAppSelectionFromStorage();

    const mergedSettings: RuntimeHostSettings = {
        ...hostSettings,
        ...storedSettings,
        ...storedUser,
        ...storedSelection,
        url: {
            ...toObject(hostSettings.url),
            ...toObject(storedSettings.url)
        },
        app: {
            ...toObject(hostSettings.app),
            ...toObject(storedSettings.app)
        },
        layout: {
            ...toObject(hostSettings.layout),
            ...toObject(storedSettings.layout)
        },
        support: {
            ...toObject(hostSettings.support),
            ...toObject(storedSettings.support)
        },
        user: {
            ...toObject(hostSettings.user),
            ...toObject(storedUser.user)
        },
        roles: {
            ...toObject(hostSettings.roles),
            ...toObject(storedUser.roles)
        },
        selection: {
            ...toObject(hostSettings.selection),
            ...toObject(storedSelection.selection)
        },
        organizations: Array.isArray(storedUser.organizations)
            ? storedUser.organizations
            : hostSettings.organizations,
        access: storedUser.access ?? hostSettings.access ?? {}
    };

    writeWindowRuntimeSettings(mergedSettings);

    return mergedSettings;
}
